/**
 * Service detail rows + file extraction — aligned with Quadro de Propostas / Request details.
 */

export const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  'bairro-solar': 'Bairro Solar',
  pap: 'Pay AS Produced (PAP)',
  transacional: 'Transacional',
  luz: 'Luz',
  gas: 'Gás',
  eletricidade: 'Eletricidade',
  mobilidade: 'Mobilidade Elétrica',
  outro: 'Outro',
};

export const FILE_FIELD_LABELS: Record<string, string> = {
  fatura: 'Fatura',
  printTelhado: 'Print do Telhado',
  declaracaoInicioAtividade: 'CRC ou Início de Atividade',
  inicioAtividade: 'CRC ou Início de Atividade',
};

const FILE_METADATA_SUFFIX =
  /_(name|size|type|data|url|public_id|publicId|lead_file_id|LeadFileId|file_id)$/i;

export type ProposalServiceFile = {
  name: string;
  size?: number | string;
  type?: string;
  field?: string;
  lead_file_id?: number | string;
  lead_id?: number | string;
  file_id?: number | string;
  public_id?: string;
  url?: string;
  data?: string;
};

export type ServiceDetailRow = {
  key: string;
  label: string;
  value: string;
  href?: string;
};

export type ServiceDetailGroup = {
  id: string;
  title: string;
  rows: ServiceDetailRow[];
  files: ProposalServiceFile[];
  leadId: number | null;
};

export function serviceDisplayName(serviceType: string): string {
  return SERVICE_DISPLAY_NAMES[serviceType] || serviceType.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatFileSize(bytes: number | string | undefined): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function isFileMetadataKey(key: string): boolean {
  if (key === 'files') return true;
  if (FILE_METADATA_SUFFIX.test(key)) return true;
  if (/^(fatura|printTelhado|declaracaoInicioAtividade|inicioAtividade)(Name|Size|Type|Data|Url|PublicId|LeadFileId)/i.test(key)) {
    return true;
  }
  return false;
}

export function resolveLeadId(
  formData: Record<string, unknown>,
  proposal?: Record<string, unknown> | null
): number | null {
  const candidates = [
    proposal?.lead_id,
    proposal?.leadId,
    formData.lead_id,
    formData.leadId,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function extractFilesFromServiceDetails(
  details: Record<string, unknown>,
  serviceId: string,
  fileRefs: unknown,
  leadId: number | null
): ProposalServiceFile[] {
  const files: ProposalServiceFile[] = [];
  const seenFileIds = new Set<string | number>();
  const seenFileNames = new Set<string>();
  const refs = fileRefs && typeof fileRefs === 'object' && !Array.isArray(fileRefs)
    ? (fileRefs as Record<string, Record<string, unknown>>)
    : {};

  const isDuplicate = (fileObj: ProposalServiceFile): boolean => {
    if (fileObj.lead_file_id != null && seenFileIds.has(fileObj.lead_file_id)) return true;
    const fileName = fileObj.name;
    if (fileName && seenFileNames.has(fileName)) return true;
    return files.some((f) => f.name === fileName && f.field === fileObj.field);
  };

  const addFile = (fileObj: ProposalServiceFile) => {
    if (!fileObj.name?.trim()) return;
    if (isDuplicate(fileObj)) return;
    files.push({ ...fileObj, lead_id: fileObj.lead_id ?? leadId ?? undefined });
    if (fileObj.lead_file_id != null) seenFileIds.add(fileObj.lead_file_id);
    if (fileObj.name) seenFileNames.add(fileObj.name);
  };

  const serviceRefs = refs[serviceId];
  if (serviceRefs && typeof serviceRefs === 'object') {
    Object.entries(serviceRefs).forEach(([field, fileData]) => {
      if (!fileData || typeof fileData !== 'object' || Array.isArray(fileData)) return;
      const fd = fileData as Record<string, unknown>;
      const fileName = String(
        fd.name || fd.file_name || fd.filename || fd.originalName || fd.original_name || ''
      ).trim();
      if (!fileName) return;
      addFile({
        name: fileName,
        size: (fd.size || fd.file_size) as number | string | undefined,
        type: String(fd.type || fd.file_type || fd.content_type || fd.mime_type || ''),
        url: String(fd.url || fd.file_url || fd.download_url || '') || undefined,
        public_id: String(fd.public_id || fd.publicId || fd.cloudinary_id || '') || undefined,
        lead_file_id: (fd.lead_file_id || fd.leadFileId || fd.file_id || fd.id) as number | string | undefined,
        lead_id: (fd.lead_id || fd.leadId) as number | string | undefined,
        data: String(fd.data || fd.file_data || '') || undefined,
        field,
      });
    });
  }

  const detailsFiles = details.files;
  if (Array.isArray(detailsFiles)) {
    detailsFiles.forEach((file: unknown) => {
      if (!file || typeof file !== 'object') return;
      const f = file as Record<string, unknown>;
      const fileName = String(f.name || f.file_name || f.filename || '').trim();
      if (!fileName) return;
      addFile({
        name: fileName,
        size: f.size as number | string | undefined,
        type: String(f.type || f.file_type || ''),
        url: String(f.url || f.file_url || '') || undefined,
        public_id: String(f.public_id || f.publicId || '') || undefined,
        lead_file_id: (f.lead_file_id || f.leadFileId || f.file_id || f.id) as number | string | undefined,
        field: String(f.field || 'unknown'),
      });
    });
  }

  const fileFields = ['fatura', 'printTelhado', 'declaracaoInicioAtividade', 'inicioAtividade'];
  fileFields.forEach((field) => {
    const fileName = String(
      details[`${field}_name`] || details[`${field}Name`] || ''
    ).trim();
    if (!fileName) return;
    if (serviceRefs?.[field]) return;
    if (seenFileNames.has(fileName)) return;
    addFile({
      name: fileName,
      size: (details[`${field}_size`] || details[`${field}Size`]) as number | string | undefined,
      type: String(details[`${field}_type`] || details[`${field}Type`] || ''),
      url: String(details[`${field}_url`] || details[`${field}Url`] || '') || undefined,
      public_id: String(details[`${field}_public_id`] || details[`${field}PublicId`] || '') || undefined,
      lead_file_id: (details[`${field}_lead_file_id`] || details[`${field}LeadFileId`]) as
        | number
        | string
        | undefined,
      data: String(details[`${field}_data`] || details[`${field}Data`] || '') || undefined,
      field,
    });
  });

  return files;
}

const pushRow = (
  rows: ServiceDetailRow[],
  key: string,
  label: string,
  value: unknown
) => {
  if (value == null || value === '') return;
  const text = String(value).trim();
  if (!text) return;
  rows.push({ key, label, value: text });
};

const formatPotencia = (details: Record<string, unknown>): string | null => {
  const raw = details.potencia;
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (s.toLowerCase() === 'outra') {
    return String(details.potenciaOutra || s).trim() || s;
  }
  return s.toUpperCase();
};

const formatDataFecho = (raw: unknown): string | null => {
  if (!raw) return null;
  try {
    const date = new Date(String(raw));
    if (Number.isNaN(date.getTime())) return String(raw);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return String(raw);
  }
};

const formatFormaPagamento = (raw: unknown): string | null => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (s === 'pronto') return 'Pronto Pagamento';
  if (s === 'prestacoes') return 'Prestações';
  return s;
};

const formatSistema = (raw: unknown): string | null => {
  if (!raw) return 'Não indicado';
  const s = String(raw).trim();
  if (s === 'trifasico') return 'Trifásico';
  if (s === 'monofasico') return 'Monofásico';
  return s;
};

/** Field rows matching Quadro de Propostas «Detalhes do Pedido». */
export function buildProposalServiceDetailRows(
  serviceType: string,
  details: Record<string, unknown>,
  ctx: {
    empresa?: Record<string, unknown>;
    isRoadtrip?: boolean;
  }
): ServiceDetailRow[] {
  const rows: ServiceDetailRow[] = [];
  const empresa = ctx.empresa || {};
  const norm = serviceType.toLowerCase().replace(/\s+/g, '-');

  const morada = details.moradaFornecimento || empresa.moradaFornecimento;
  pushRow(rows, 'moradaFornecimento', 'Morada de fornecimento', morada);

  if (['bairro-solar', 'pap', 'luz', 'transacional', 'eletricidade'].includes(norm)) {
    pushRow(rows, 'cpe', 'CPE', details.cpe);
    const potencia = formatPotencia(details);
    if (potencia) pushRow(rows, 'potencia', 'Potência', potencia);
    if (details.potencia === 'outra' && details.potenciaOutra) {
      pushRow(rows, 'potenciaOutra', 'Potência (outra)', details.potenciaOutra);
    }
    if (ctx.isRoadtrip && details.dataFecho) {
      pushRow(rows, 'dataFecho', 'Data prevista para o fecho', formatDataFecho(details.dataFecho));
    }
    if (norm === 'bairro-solar') {
      pushRow(rows, 'coordenadasGeograficas', 'Coordenadas geográficas', details.coordenadasGeograficas);
    }
    if (norm === 'luz' || norm === 'eletricidade') {
      pushRow(rows, 'sistema', 'Sistema', formatSistema(details.sistema));
    }
    pushRow(rows, 'observacoes', 'Observações', details.observacoes);
  }

  if (norm === 'transacional') {
    const fp = formatFormaPagamento(details.formaPagamento);
    if (fp) pushRow(rows, 'formaPagamento', 'Forma de pagamento', fp);
  }

  if (norm === 'gas') {
    pushRow(rows, 'cui', 'CUI', details.cui);
    if (details.escalao != null && details.escalao !== '') {
      pushRow(rows, 'escalao', 'Escalão', `Escalão ${details.escalao}`);
    }
    pushRow(rows, 'comercializadora', 'Comercializadora', details.comercializadora);
    pushRow(rows, 'valorFatura', 'Valor da fatura', details.valorFatura);
    pushRow(rows, 'observacoes', 'Observações', details.observacoes);
  }

  if (norm === 'mobilidade') {
    const mobilidadeLabels: Record<string, string> = {
      plataformaGestao: 'Selecione a plataforma de gestão',
      localInstalacao: 'Onde será realizada a instalação?',
      quadroMobilidade: 'Pretende ter quadro de mobilidade elétrica?',
      tipoFixacao: 'Qual o tipo de fixação?',
      tipoCarregamento:
        'Qual o tipo de carregamento que melhor se adequa à sua frota?',
      distanciaQveProximo: 'Distância do QVE/QE ao lugar mais próximo',
      distanciaQveDistante: 'Distância do QVE/QE ao lugar mais distante',
    };
    const mobilidadeOptions: Record<string, Record<string, string>> = {
      plataformaGestao: {
        'sem-gestao': 'Sem gestão',
        'edp-charge': 'EDP Charge',
      },
      localInstalacao: { interior: 'Interior', exterior: 'Exterior' },
      quadroMobilidade: { sim: 'Sim', nao: 'Não' },
      tipoFixacao: { solo: 'Solo', parede: 'Parede' },
      tipoCarregamento: {
        normal:
          'Normal: carrega 100 Km entre 4 horas e 40 min [3,7 - 22kVA]',
        rapido: 'Rápido: carrega 100Km em 20 min (45 kW)',
      },
    };
    const formatMobilidade = (key: string, value: unknown): string => {
      if (value == null || value === '') return '';
      const raw = String(value);
      return mobilidadeOptions[key]?.[raw] || raw;
    };
    (
      [
        'plataformaGestao',
        'localInstalacao',
        'quadroMobilidade',
        'tipoFixacao',
        'tipoCarregamento',
        'distanciaQveProximo',
        'distanciaQveDistante',
      ] as const
    ).forEach((key) => {
      pushRow(rows, key, mobilidadeLabels[key], formatMobilidade(key, details[key]));
    });
    // Legacy fields (older submissions)
    pushRow(rows, 'tipoCarregador', 'Tipo de carregador', details.tipoCarregador);
    if (!details.tipoCarregamento) {
      pushRow(rows, 'potencia', 'Potência', details.potencia);
    }
    pushRow(rows, 'observacoes', 'Observações', details.observacoes);
  }

  if (norm === 'outro') {
    Object.entries(details).forEach(([key, value]) => {
      if (isFileMetadataKey(key)) return;
      if (value == null || value === '') return;
      if (typeof value === 'object') return;
      const label = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
      pushRow(rows, key, label, value);
    });
  }

  return rows;
}

export function buildProposalServiceGroups(
  formData: Record<string, unknown>,
  proposal?: Record<string, unknown> | null
): ServiceDetailGroup[] {
  const servicosRaw = formData.servicos;
  const serviceDetails = (formData.serviceDetails || {}) as Record<string, Record<string, unknown>>;
  const fileReferences = formData.fileReferences;
  const empresa = (formData.empresa || proposal?.empresa || {}) as Record<string, unknown>;
  const equipaInterna = (formData.equipaInterna || {}) as Record<string, unknown>;
  const isRoadtrip = equipaInterna.tipoPedido === 'roadtrip';
  const leadId = resolveLeadId(formData, proposal);

  const list = Array.isArray(servicosRaw) && servicosRaw.length > 0 ? servicosRaw : null;
  if (!list) return [];

  const groups: ServiceDetailGroup[] = [];

  list.forEach((inst: unknown, idx: number) => {
    if (!inst || typeof inst !== 'object') return;
    const item = inst as Record<string, unknown>;
    const serviceType = String(item.service || item.produto || 'servico');
    const instanceId = String(item.instanceId || `svc-${idx}`);
    const instanceNumber = Number(item.instanceNumber) || idx + 1;
    const details = serviceDetails[instanceId] || serviceDetails[serviceType] || {};

    const rows = buildProposalServiceDetailRows(serviceType, details, { empresa, isRoadtrip });
    const files = extractFilesFromServiceDetails(details, instanceId, fileReferences, leadId);

    if (!rows.length && !files.length) return;

    const baseName = serviceDisplayName(serviceType);
    const title =
      list.length > 1 ? `Serviço ${instanceNumber}: ${baseName}` : baseName;

    groups.push({
      id: `servico-${instanceId}`,
      title,
      rows,
      files,
      leadId,
    });
  });

  return groups;
}
